import { useContext, useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { FrameSlot, StaffFrame, pageArea, type StaffContext } from './frame.tsx';
import { FRAME, Level, ScreenContext, useFrame, type ScreenKind } from './scope.ts';
import { strings } from './strings.ts';

/**
 * The page frame and the layout pieces every page composes. A page says what
 * a screen is (`kind`); the kit derives the rest from MIX.md: office and
 * guest screens are CALM, counter and wall screens RUSH, a wall (the KDS)
 * on the dark ground. No page names a density, so none can get it wrong.
 *
 * Screen sets the density and the ground on <html>, not on its own element:
 * Radix renders dialogs and menus outside the page, and they must read the
 * same tokens.
 */
export type { ScreenKind } from './scope.ts';

export function Heading({ level, id, className, children }: { readonly level: 1 | 2 | 3 | 4; readonly id?: string; readonly className: string; readonly children: ReactNode }) {
  const H = `h${String(level)}` as 'h1' | 'h2' | 'h3' | 'h4';
  return (
    <H id={id} className={className}>
      {children}
    </H>
  );
}

/** A sentence whose figures stand out: words, and `{ figure }` parts drawn bold. A strings function returns it, so word order stays the language's. */
export type Words = readonly (string | { readonly figure: string })[];

export function Sentence({ words }: { readonly words: Words }) {
  return (
    <>
      {words.map((part, at) =>
        typeof part === 'string' ? (
          part
        ) : (
          <strong key={at} className="font-bold text-ink tabular-nums">
            {part.figure}
          </strong>
        ),
      )}
    </>
  );
}

export function Screen({
  kind,
  title,
  lead,
  leadTestId,
  summary,
  staff,
  wide = false,
  bar,
  hero,
  focusTitle = false,
  crumb,
  badge,
  nav,
  actions,
  children,
}: {
  readonly kind: ScreenKind;
  readonly title: string;
  /** The line under the title: words, or words with a token and an inline action in them. */
  readonly lead?: ReactNode;
  /** A test id on the lead line (what it names may be checked by a journey). */
  readonly leadTestId?: string;
  /** The one sentence under the title that carries the page's numbers (`Words`), in place of a lead. */
  readonly summary?: Words;
  /** A staff screen: drawn in the staff frame (sidebar, top bar, status bar) for this group and who is signed in. */
  readonly staff?: StaffContext;
  /** A screen of records (a table): the frame's full width, not a reading column. */
  readonly wide?: boolean;
  /** BrandBar on a guest screen; a bar of its own on a staff screen without `staff` (sign-in). */
  readonly bar?: ReactNode;
  /** A guest screen's band that holds the title itself (MemberHero): drawn in place of the bar and the title. */
  readonly hero?: ReactNode;
  /** The title takes focus when it changes: a step that answers what the person did (a screen reader reads it). */
  readonly focusTitle?: boolean;
  /** Tabs under the bar, on a screen outside the staff frame. */
  readonly nav?: ReactNode;
  /** Where this screen sits under another (Campaigns / a campaign): the way back, above the title. */
  readonly crumb?: { readonly label: string; readonly onBack: () => void };
  /** A status beside the title (a StatusChip). */
  readonly badge?: ReactNode;
  /** Buttons about the whole screen, beside its title: the primary action last. */
  readonly actions?: ReactNode;
  readonly children: ReactNode;
}) {
  const frame = FRAME[kind];
  // Under StaffLayout the frame is already on screen: this page tells it who and where, and draws only the page area.
  const slot = useContext(FrameSlot);
  // A band that holds the title is a member's own page: its look (kit.css).
  useFrame(kind, hero === undefined ? undefined : 'member', slot === null);
  useLayoutEffect(() => {
    if (slot !== null && staff !== undefined) slot({ staff, kind });
  }, [slot, kind, staff]);
  useEffect(() => {
    document.title = `${title} - ${strings.staff.product}`;
  }, [title]);
  const titleRef = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    if (focusTitle) titleRef.current?.focus();
  }, [focusTitle, title]);
  const head = hero === undefined && (
    <div className="flex flex-wrap items-end justify-between gap-x-s5 gap-y-s4">
      <div className="flex min-w-0 flex-col gap-s2">
        {crumb !== undefined && (
          <nav aria-label={strings.shell.crumbs} className="flex items-center gap-s2 text-ink-2">
            <button type="button" className="cursor-pointer font-semibold text-brand hover:underline focus-visible:outline-2 focus-visible:outline-brand" onClick={crumb.onBack}>
              {crumb.label}
            </button>
            <span aria-hidden="true">/</span>
            <span className="min-w-0 truncate" aria-current="page">
              {title}
            </span>
          </nav>
        )}
        <div className="flex flex-wrap items-center gap-s3">
        <h1 ref={titleRef} tabIndex={focusTitle ? -1 : undefined} className={staff === undefined && slot === null ? 'text-title font-bold outline-none' : 'text-page leading-[1.15] font-bold'}>
          {title}
        </h1>
          {badge}
        </div>
        {summary !== undefined && (
          <p className="text-strong text-ink-2">
            <Sentence words={summary} />
          </p>
        )}
        {lead !== undefined && (
          <p className="text-strong text-ink-2" data-testid={leadTestId}>
            {lead}
          </p>
        )}
      </div>
      {actions !== undefined && actions !== false && <div className="flex flex-wrap items-center gap-s3">{actions}</div>}
    </div>
  );
  const body = <Level.Provider value={2}>{children}</Level.Provider>;
  if (slot !== null)
    return (
      <ScreenContext.Provider value={kind}>
        <main className={pageArea(wide)}>
          {head}
          {body}
        </main>
      </ScreenContext.Provider>
    );
  if (staff !== undefined)
    return (
      <ScreenContext.Provider value={kind}>
        <StaffFrame staff={staff} kind={kind}>
          <main className={pageArea(wide)}>
            {head}
            {body}
          </main>
        </StaffFrame>
      </ScreenContext.Provider>
    );
  return (
    <ScreenContext.Provider value={kind}>
      <div className="flex min-h-dvh flex-col bg-ground font-sans text-body text-ink antialiased">
        {hero ?? bar}
        {nav}
        <main className={`mx-auto flex w-full flex-col gap-s5 px-s4 pt-s5 pb-s7 ${frame.width}`}>
          {head}
          {body}
        </main>
      </div>
    </ScreenContext.Provider>
  );
}

/** How long a Toast stays, and how long it takes to fade. */
const TOAST_MS = 4000;
const TOAST_FADE_MS = 120;

/**
 * A short note at the bottom of the screen, centred, that goes away on its
 * own: what just happened (a join, a sign-in), never something to act on, so
 * it takes no tap. It rises in from the bottom, clear of the iPhone home
 * indicator (the safe area); with reduced motion it shows and goes without moving.
 */
export function Toast({ text }: { readonly text: string }) {
  const [phase, setPhase] = useState<'shown' | 'leaving' | 'gone'>('shown');
  const [said, setSaid] = useState(false);
  useEffect(() => {
    setSaid(true);
    const leave = setTimeout(() => {
      setPhase('leaving');
    }, TOAST_MS);
    const gone = setTimeout(() => {
      setPhase('gone');
    }, TOAST_MS + TOAST_FADE_MS);
    return () => {
      clearTimeout(leave);
      clearTimeout(gone);
    };
  }, []);
  if (phase === 'gone') return null;
  return (
    <div className="pointer-events-none fixed inset-x-0 bottom-[calc(env(safe-area-inset-bottom)+var(--spacing-s5))] z-50 flex justify-center px-s4">
      <div
        role="status"
        className={`flex max-w-full items-center gap-s2 rounded-chip bg-surface py-s2 pr-s4 pl-s2 text-body font-semibold text-ink shadow-float motion-reduce:animate-none ${phase === 'leaving' ? 'animate-fade-out opacity-0' : 'animate-toast-in'}`}
      >
        <span className="flex size-s6 shrink-0 items-center justify-center rounded-chip bg-ok-soft text-ok" aria-hidden="true">
          <svg viewBox="0 0 24 24" className="size-s4">
            <path d="M5 12.5l4.5 4.5L19 7.5" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </span>
        {said ? text : null}
      </div>
    </div>
  );
}

/**
 * A titled region of a screen, boxed (`card`, MIX: surface, 1px line, 10px
 * radius, no shadow), a box inside a card (`inset`), or open (`plain`).
 * `aside` sits at the end of its title line (a StatusChip, a Remove button).
 */
export function Section({
  title,
  lead,
  aside,
  look = 'card',
  testId,
  children,
}: {
  readonly title?: string;
  readonly lead?: string;
  readonly aside?: ReactNode;
  readonly look?: 'card' | 'inset' | 'plain';
  readonly testId?: string;
  readonly children: ReactNode;
}) {
  const level = useContext(Level);
  const id = useId();
  const box = {
    card: 'flex flex-col gap-s4 rounded-card border border-line bg-surface p-s5',
    inset: 'flex flex-col gap-s4 rounded-card border border-line p-s4',
    plain: 'flex flex-col gap-s4',
  }[look];
  return (
    <section aria-labelledby={title === undefined ? undefined : id} className={box} data-testid={testId}>
      {(title !== undefined || lead !== undefined || aside !== undefined) && (
        <div className={`flex justify-between gap-s3 ${lead === undefined ? 'items-center' : 'items-start'}`}>
          <div className="flex min-w-0 flex-col gap-s1">
            {title !== undefined && (
              <Heading level={level} id={id} className={`${look === 'inset' ? 'text-strong' : 'text-heading'} font-semibold [overflow-wrap:anywhere]`}>
                {title}
              </Heading>
            )}
            {lead !== undefined && <p className="text-ink-2">{lead}</p>}
          </div>
          {aside}
        </div>
      )}
      <Level.Provider value={level === 2 ? 3 : 4}>{children}</Level.Provider>
    </section>
  );
}

export type Gap = 'tight' | 'normal' | 'loose';
const GAP = {
  tight: 'gap-s2',
  normal: 'gap-s4',
  loose: 'gap-s5',
} as const satisfies Record<Gap, string>;

export function Stack({ gap = 'normal', children }: { readonly gap?: Gap; readonly children: ReactNode }) {
  return <div className={`flex flex-col ${GAP[gap]}`}>{children}</div>;
}

/** Side by side, wrapping on a narrow screen. `fill`: fields sharing the line, each as wide as it can, one per line on a phone. */
export function Row({ gap = 'normal', justify = 'start', fill = false, children }: { readonly gap?: Gap; readonly justify?: 'start' | 'center' | 'between' | 'end'; readonly fill?: boolean; readonly children: ReactNode }) {
  const align = {
    start: 'justify-start',
    center: 'justify-center',
    between: 'justify-between',
    end: 'justify-end',
  }[justify];
  return <div className={`flex flex-wrap ${fill ? 'items-start *:min-w-[12rem] *:flex-1' : 'items-center'} ${GAP[gap]} ${align}`}>{children}</div>;
}

export type Tone = 'body' | 'lead' | 'muted' | 'caption' | 'strong' | 'figures';
const TONE = {
  body: 'text-body text-ink',
  lead: 'text-strong text-ink-2',
  muted: 'text-body text-muted',
  caption: 'text-caption text-muted',
  strong: 'text-body font-semibold text-ink',
  figures: 'text-body font-semibold text-ink tabular-nums whitespace-nowrap',
} as const satisfies Record<Tone, string>;

/** Words. Headings come from Screen and Section, so a page cannot put them out of order. `inline` for words beside words (in a Row). */
export function Text({ tone = 'body', inline = false, children }: { readonly tone?: Tone; readonly inline?: boolean; readonly children: ReactNode }) {
  return inline ? <span className={TONE[tone]}>{children}</span> : <p className={TONE[tone]}>{children}</p>;
}

/** What an action answered, read out when it appears (`role="status"`). */
export function Answer({ testId, children }: { readonly testId?: string; readonly children: ReactNode }) {
  return (
    <div className="flex flex-col gap-s3" role="status" data-testid={testId}>
      {children}
    </div>
  );
}

/** A figure with its label: condensed tabular numerals (MIX: numbers always tabular), and a note beside it. */
export function Stat({ label, value, note, testId, noteTestId }: { readonly label: string; readonly value: string; readonly note?: string; readonly testId?: string; readonly noteTestId?: string }) {
  const level = useContext(Level);
  return (
    <div className="flex flex-col gap-s3 rounded-card border border-line bg-surface p-s5">
      <Heading level={level} className="text-heading font-semibold">
        {label}
      </Heading>
      <p className="flex flex-wrap items-baseline gap-s3">
        <span className="font-figures text-page leading-none font-bold tabular-nums font-stretch-75%" data-testid={testId}>
          {value}
        </span>
        {note !== undefined && (
          <span className="text-ink-2" data-testid={noteTestId}>
            {note}
          </span>
        )}
      </p>
    </div>
  );
}

/** It worked: a green tick, what happened (the title), the lines that say what and where, and what to do next. Read out when it appears. */
export function Done({
  title,
  lines,
  children,
}: {
  readonly title: string;
  readonly lines: readonly {
    readonly text: string;
    readonly testId?: string;
    readonly strong?: boolean;
  }[];
  readonly children?: ReactNode;
}) {
  const level = useContext(Level);
  return (
    <section className="flex flex-col items-center gap-s3 rounded-card border border-line bg-surface p-s6 text-center" role="status">
      <span className="flex size-s8 items-center justify-center rounded-chip bg-ok-soft text-ok" aria-hidden="true">
        <svg viewBox="0 0 24 24" className="size-s6">
          <path d="M5 12.5l4.5 4.5L19 7.5" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </span>
      <Heading level={level} className="text-title font-bold">
        {title}
      </Heading>
      {lines.map((line) => (
        <p key={line.text} className={line.strong === true ? 'text-strong font-semibold text-ink' : 'text-ink-2'} data-testid={line.testId}>
          {line.text}
        </p>
      ))}
      {children !== undefined && <div className="flex w-full flex-col gap-s3 pt-s2">{children}</div>}
    </section>
  );
}

/** What a device screen shows centred: a code to scan, a count, one action. Changes are read out (`aria-live`). */
export function Stage({ children }: { readonly children: ReactNode }) {
  return (
    <section className="flex flex-col items-center gap-s4 rounded-card border border-line bg-surface p-s6 text-center" aria-live="polite">
      {children}
    </section>
  );
}
